import React from 'react';
import { motion, useInView } from 'framer-motion';
import { EASE_OUT, STAGGER, VARIANTS, container, withDelay, type VariantName } from '../motion';

export const EASE = EASE_OUT;

function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* The one scroll-in primitive. `variant` picks the move from the shared
   vocabulary; `stagger` turns the direct children into a sequence.

   `data-inview` is written on the wrapper either way, which is what the
   draw-in rules and other CSS-driven marks key off. */
export function Reveal({
    children,
    delay = 0,
    variant = 'fadeUp',
    stagger,
    once = true,
    className = '',
}: {
    children: React.ReactNode;
    delay?: number;
    variant?: VariantName;
    stagger?: number | keyof typeof STAGGER;
    once?: boolean;
    className?: string;
}) {
    const [reduced] = React.useState(prefersReducedMotion);
    const ref = React.useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once, margin: '-10% 0px -10% 0px' });
    const [shown, setShown] = React.useState(false);

    React.useEffect(() => {
        // Reveal anything already on screen at mount so first paint is never blank.
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 80 && rect.bottom > 80) setShown(true);
    }, []);

    if (reduced) {
        return (
            <div className={className} data-inview="true">
                {children}
            </div>
        );
    }

    const on = inView || shown;
    const step = typeof stagger === 'string' ? STAGGER[stagger] : stagger;
    const move = VARIANTS[variant];

    if (step !== undefined) {
        return (
            <motion.div
                ref={ref}
                className={className}
                data-inview={on ? 'true' : 'false'}
                variants={container(step, delay)}
                initial="hidden"
                animate={on ? 'shown' : 'hidden'}
            >
                {React.Children.map(children, (child, i) =>
                    React.isValidElement(child) || typeof child === 'string' ? (
                        <motion.div key={i} variants={move}>
                            {child}
                        </motion.div>
                    ) : (
                        child
                    ),
                )}
            </motion.div>
        );
    }

    return (
        <motion.div
            ref={ref}
            className={className}
            data-inview={on ? 'true' : 'false'}
            variants={withDelay(move, delay)}
            initial="hidden"
            animate={on ? 'shown' : 'hidden'}
        >
            {children}
        </motion.div>
    );
}
